import React, { useMemo } from 'react';

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import {
  CardActionArea,
  CardContent,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';

import { LessonEnrolment } from '../../services/classes/types';
import { Course } from '../../services/courses/types';
import {
  getLessonEnrolmentClassName, getLessonEnrolmentCourse, getLessonEnrolmentLabel,
} from '../../utils/common/classes';
import { getColorKeyFromCourse } from '../../utils/common/courses';
import Chip from '../Chip';

import {
  LessonLabel,
  LessonTime,
  StyledCard,
} from './index.styles';

interface LessonEnrolmentCardProps {
  isInvoiced: boolean;
  isExtra: boolean;
  lessonEnrolment: LessonEnrolment;
  courses: Course[];
  showFullDate?: boolean;
  hideClassName?: boolean;
  selected?: boolean;
  onSelect?: () => void,
  greyedOut?: boolean,
}

const LessonEnrolmentCard = ({
  isInvoiced,
  isExtra,
  lessonEnrolment,
  courses,
  hideClassName = false,
  showFullDate = true,
  selected = false,
  onSelect = () => {},
  greyedOut = false,
}: LessonEnrolmentCardProps) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const { startDatetime } = lessonEnrolment;

  // Split course from lesson as the string before the first #
  const course = getLessonEnrolmentCourse(lessonEnrolment);

  const className = getLessonEnrolmentClassName(lessonEnrolment);
  const label = getLessonEnrolmentLabel(lessonEnrolment, courses);
  const color = getColorKeyFromCourse(course, courses) ?? 'primary';

  const invoiceChipLabel = useMemo(() => {
    if (isExtra) {
      return isInvoiced ? 'Extra (Invoiced)' : 'Extra (Not invoiced)';
    } if (isInvoiced) {
      return 'Invoiced';
    }
    return 'Not invoiced';
  }, [isInvoiced, isExtra]);

  return (
    <StyledCard elevation={0} $selected={selected} $greyedOut={greyedOut}>
      <CardActionArea onClick={onSelect}>
        <CardContent>
          <Grid container spacing={2} direction="row">
            <Grid item container xs="auto" spacing={2}>
              <Grid item xs="auto" container direction="column">
                {showFullDate && (
                  <Grid item>
                    <Typography color={theme.palette.gray.main}>
                      {dayjs(startDatetime).format('D MMM YYYY, ddd h:mm a')}
                    </Typography>
                  </Grid>
                ) }
                <Grid container spacing={3}>
                  {!showFullDate && (
                    <Grid
                      item
                      xs="auto"
                      direction="column"
                      container
                      justifyContent="center"
                    >
                      <LessonTime variant="h5">{dayjs(startDatetime).format('h:mm a')}</LessonTime>
                    </Grid>
                  )}
                  <Grid
                    item
                    xs="auto"
                    direction="column"
                    container
                    justifyContent="center"
                  >
                    <LessonLabel variant="h5" $color={color}>{label}</LessonLabel>
                    {!hideClassName && <Typography>{className}</Typography>}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {lessonEnrolment.rollMarked && (
              <Grid item container xs justifyContent="flex-end" spacing={1}>
                {lessonEnrolment.present ? <CheckCircleOutlinedIcon color="success" /> : <CancelOutlinedIcon color="error" />}
              </Grid>
            )}
          </Grid>
          <Stack
            direction="row"
            spacing={1}
            sx={isMd ? {
              position: 'absolute',
              right: theme.spacing(2),
              bottom: theme.spacing(2),
            } : { marginTop: theme.spacing(1) }}
          >
            {lessonEnrolment.enrolmentMethod === 'MAKEUP' && (
              <Chip
                color="lightOrange"
                label="Makeup"
                allCaps
                cursorType="pointer"
              />
            )}
            <Chip
              variant={isInvoiced ? 'filled' : 'outlined'}
              color={isExtra ? 'success' : 'gray'}
              label={invoiceChipLabel}
              allCaps
              cursorType="pointer"
            />
          </Stack>
        </CardContent>
      </CardActionArea>
    </StyledCard>
  );
};
export default LessonEnrolmentCard;
